// src/pages/InventoryRMConsumption.js
import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Paper,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Alert,
  Typography,
  Grid,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';

import Client from '../Client';
import Config from '../ClientConfig';

class RawMaterialConsumption extends React.Component {
  constructor(props) {
    super(props);

    const today = dayjs().format('YYYY-MM-DD');

    this.state = {
      client: new Client('none'),
      loading: false,

      // lookups
      locations: [],
      products: [],
      productNameMap: {},

      // filters
      filters: {
        date: today,
        location: '',
        product: '',
      },
      filtersCollapsed: false,

      // summary visibility
      summaryCollapsed: false,

      // API data
      daily: [],
      openingStock: 0,
      theoreticalTotal: 0,
      unitLabel: '',

      // totals
      totalStockInBase: 0,
      totalStockInAdjusted: 0,
      totalStockOutTransfers: 0,
      totalStockOutDisplay: 0,
      totalAdjustments: 0,

      // per-day adjustments (date -> number or string while typing)
      adjustmentsByDate: {},

      // user input: closing stock
      closingStockInput: '',

      // derived metrics
      realConsumption: null,
      varianceQty: null,
      variancePct: null,
      closingTheoretical: null,

      // UI
      page: 0,
      rowsPerPage: -1, // always show ALL rows; table scrolls
      tableHeight: 400,

      infoText: '', // used for CSV only, hidden in UI
      errorText: '',
    };

    this.tableContainerRef = React.createRef();
  }

  componentDidMount() {
    this.fetchLookups();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateTableLayout);
    }
    setTimeout(this.updateTableLayout, 0);
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateTableLayout);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.filtersCollapsed !== this.state.filtersCollapsed ||
      prevState.summaryCollapsed !== this.state.summaryCollapsed ||
      prevState.daily !== this.state.daily
    ) {
      this.updateTableLayout();
    }
  }

  // ---------- Layout helper: auto height (rows always "all") ----------

  updateTableLayout = () => {
    // Height is controlled by parent flex layout.
    // Kept as no-op to avoid changing existing lifecycle calls.
  };

  // ---------- Utils ----------

  escapeCSV(value) {
    const s = value == null ? '' : String(value);
    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  }

  formatQtyDisplay = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return '';
    if (Math.abs(n - Math.trunc(n)) < 1e-9) {
      return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    }
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  };

  formatQtyCsv = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return '';
    if (Math.abs(n - Math.trunc(n)) < 1e-9) return String(Math.trunc(n));
    return n.toFixed(3).replace(/\.?0+$/, '');
  };

  // ---------- Lookups ----------

  fetchLookups() {
    this.setState({ loading: true, errorText: '', infoText: '' });
    try {
      const locationsResp =
        this.state.client.getData(
          (Config.API_URL_LOCATIONS || '') + 'list_locations/',
          { status: 1 }
        ) || {};

      const productsResp =
        this.state.client.getData(
          (Config.API_URL_PRODUCT || '') + 'list_products/',
          { status: 2, isPurchased: 1, orderBy_name: true }
        ) || {};

      const locs = Array.isArray(locationsResp.locations)
        ? locationsResp.locations
        : [];
      const locations = locs.sort((a, b) => {
        const an = ((a && (a.name || a.description)) || '').toLowerCase();
        const bn = ((b && (b.name || b.description)) || '').toLowerCase();
        return an.localeCompare(bn);
      });

      const products = Array.isArray(productsResp.products)
        ? productsResp.products
        : [];

      const productNameMap = {};
      for (const p of products) {
        const pid = p?.iref || p?.uid || p?.code || p?.id || p?.product;
        const pname =
          p?.name ||
          p?.productName ||
          p?.description ||
          p?.code ||
          pid;
        if (pid) productNameMap[String(pid)] = String(pname);
      }

      this.setState({ locations, products, productNameMap, loading: false });
    } catch (e) {
      this.setState({
        loading: false,
        errorText: 'Failed to load lookups.',
      });
    }
  }

  // ---------- Query + fetch ----------

  buildQuery() {
    const f = this.state.filters || {};
    const date = f.date || dayjs().format('YYYY-MM-DD');
    const q = { asof: date };
    if (f.location) q.location = f.location;
    if (f.product) q.product = f.product;
    return q;
  }

  fetchData = () => {
    const { filters, client } = this.state;

    if (!filters.location || !filters.product) {
      this.setState({
        daily: [],
        openingStock: 0,
        theoreticalTotal: 0,
        totalStockInBase: 0,
        totalStockInAdjusted: 0,
        totalStockOutTransfers: 0,
        totalStockOutDisplay: 0,
        totalAdjustments: 0,
        realConsumption: null,
        varianceQty: null,
        variancePct: null,
        adjustmentsByDate: {},
        page: 0,
        infoText: '',
        errorText: 'Please select both Location and Product.',
      });
      return;
    }

    this.setState({
      loading: true,
      page: 0,
      infoText: '',
      errorText: '',
      daily: [],
      adjustmentsByDate: {},
      closingStockInput: '',
      realConsumption: null,
      varianceQty: null,
      variancePct: null,
    });

    const q = this.buildQuery();
    const baseUrl =
      Config.API_URL_REPORTS_TRANSACTIONS || Config.API_URL_REPORTS || '';
    const url = baseUrl + 'rm_consumption/';

    let resp = {};
    try {
      resp = client.getData(url, q) || {};
    } catch (e) {
      this.setState({ loading: false, errorText: 'Request failed.' });
      return;
    }

    if (resp && resp.error) {
      this.setState({
        loading: false,
        errorText: resp.detail || resp.error || 'Request failed.',
      });
      return;
    }

    const daily = Array.isArray(resp.daily) ? resp.daily : [];
    const openingStock = Number(
      resp.opening_stock != null ? resp.opening_stock : 0
    );
    const theoreticalTotal = Number(
      resp.theoretical_total != null ? resp.theoretical_total : 0
    );

    const productInfo = (resp.filters && resp.filters.product) || {};
    const unitLabel = productInfo.uom || productInfo.unit || '';

    let totalStockInBase = 0;
    let totalStockOutTransfers = 0;

    daily.forEach((d) => {
      const inBase =
        Number(d.stock_in_receipts || 0) +
        Number(d.stock_in_transfers || 0);
      const outTransfers = Number(d.stock_out_transfers || 0);
      totalStockInBase += inBase;
      totalStockOutTransfers += outTransfers;
    });

    const totalAdjustments = 0;
    const totalStockInAdjusted = totalStockInBase + totalAdjustments;
    const totalStockOutDisplay = theoreticalTotal + totalStockOutTransfers;

    this.setState(
      {
        loading: false,
        daily,
        openingStock,
        theoreticalTotal,
        unitLabel: unitLabel || '',
        totalStockInBase,
        totalStockInAdjusted,
        totalStockOutTransfers,
        totalStockOutDisplay,
        totalAdjustments,
        infoText: daily.length
          ? `Period from ${resp.filters?.start || ''} to ${
              resp.filters?.end || ''
            }`
          : '',
      },
      () => {
        this.recomputeDerivedMetrics();
        this.updateTableLayout();
      }
    );
  };

  // ---------- Derived metrics ----------

  recomputeDerivedMetrics = () => {
    const {
      openingStock,
      theoreticalTotal,
      totalStockInBase,
      totalStockOutTransfers,
      closingStockInput,
      adjustmentsByDate,
    } = this.state;

    // Sum adjustments by day
    let totalAdjustments = 0;
    if (adjustmentsByDate) {
      Object.values(adjustmentsByDate).forEach((v) => {
        const n = Number(v);
        if (Number.isFinite(n)) totalAdjustments += n;
      });
    }

    const stockInAdjusted = totalStockInBase + totalAdjustments;
    const totalStockOutDisplay = theoreticalTotal + totalStockOutTransfers;

    const closingStr = (closingStockInput || '').trim();

    // Base state when closing is empty or invalid
    const baseState = {
      totalAdjustments,
      totalStockInAdjusted: stockInAdjusted,
      totalStockOutDisplay,
      realConsumption: null,
      closingTheoretical: null,
      varianceQty: null,
      variancePct: null,
    };

    if (closingStr === '') {
      this.setState(baseState);
      return;
    }

    const closing = Number(closingStr.replace(',', '.'));
    if (!Number.isFinite(closing)) {
      this.setState(baseState);
      return;
    }

    // Real OUT = what actually left as consumption/loss
    const realConsumption =
      Number(openingStock || 0) +
      Number(stockInAdjusted || 0) -
      Number(totalStockOutTransfers || 0) -
      closing;

    // Theoretical FINAL stock = Initial + In - Transfers - Script Out
    const closingTheoretical =
      Number(openingStock || 0) +
      Number(stockInAdjusted || 0) -
      Number(totalStockOutTransfers || 0) -
      Number(theoreticalTotal || 0);

    // Var = Final (real) - Final (script)
    const varianceQty = closing - closingTheoretical;
    // const varianceQty = realConsumption - theoreticalTotal;
    const variancePct =
      closingTheoretical && Math.abs(closingTheoretical) > 1e-9
        ? (varianceQty / closingTheoretical) * 100.0
        : null;

    this.setState({
      totalAdjustments,
      totalStockInAdjusted: stockInAdjusted,
      totalStockOutDisplay,
      realConsumption,
      closingTheoretical,
      varianceQty,
      variancePct,
    });
  };

  onChangeClosing = (raw) => {
    raw = raw || '';
    raw = raw.replace(',', '.');
    if (raw === '') {
      this.setState({ closingStockInput: '' }, this.recomputeDerivedMetrics);
      return;
    }
    if (raw === '-' || raw === '+') {
      this.setState({ closingStockInput: raw }, this.recomputeDerivedMetrics);
      return;
    }
    const decimalPattern = /^[-+]?\d*(\.\d{0,3})?$/;
    if (!decimalPattern.test(raw)) {
      return; // Reject invalid characters
    }
    this.setState({ closingStockInput: raw }, this.recomputeDerivedMetrics);
  };

  onChangeAdjustment = (dateStr, valueStr) => {
    let raw = valueStr || '';
    raw = raw.replace(',', '.');
    this.setState(
      (prev) => {
        const adjustmentsByDate = { ...(prev.adjustmentsByDate || {}) };
        if (raw === '') {
          delete adjustmentsByDate[dateStr];
          return { adjustmentsByDate };
        }
        if (raw === '-' || raw === '+') {
          adjustmentsByDate[dateStr] = raw;
          return { adjustmentsByDate };
        }
        const decimalPattern = /^[-+]?\d*(\.\d{0,3})?$/;
        if (!decimalPattern.test(raw)) {
          return { adjustmentsByDate };
        }
        if (raw.endsWith('.')) {
          adjustmentsByDate[dateStr] = raw;
          return { adjustmentsByDate };
        }
        const n = Number(raw);
        if (Number.isFinite(n)) {
          adjustmentsByDate[dateStr] = n;
        } else {
          adjustmentsByDate[dateStr] = raw;
        }
        return { adjustmentsByDate };
      },
      this.recomputeDerivedMetrics
    );
  };

  // ---------- CSV ----------

  exportCSV = () => {
    const st = this.state;
    const filters = st.filters || {};
    const locations = st.locations || [];
    const productNameMap = st.productNameMap || {};
    const daily = st.daily || [];
    const adjustmentsByDate = st.adjustmentsByDate || {};

    const asof = dayjs(
      filters.date || dayjs().format('YYYY-MM-DD')
    ).format('YYYY-MM-DD');

    const locId = filters.location;
    const locObj = (locations || []).find(
      (l) => String(l.iref || l.uid || l.id) === String(locId)
    );
    const locationLabel = locId
      ? (locObj && (locObj.name || locObj.description)) || String(locId)
      : 'Unknown';

    const prodId = filters.product;
    const productLabel = prodId
      ? productNameMap[String(prodId)] || String(prodId)
      : 'Unknown';

    const generatedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');

    const summaryRows = [
      ['Summary'],
      ['Date', asof],
      ['Location', locationLabel],
      ['Product (RM)', productLabel],
      ['Opening stock', this.formatQtyCsv(st.openingStock)],
      ['Stock IN (base)', this.formatQtyCsv(st.totalStockInBase)],
      ['Adjustments', this.formatQtyCsv(st.totalAdjustments)],
      ['Stock IN (adjusted)', this.formatQtyCsv(st.totalStockInAdjusted)],
      ['Theoretical consumption (Script Out)', this.formatQtyCsv(st.theoreticalTotal)],
      ['Stock OUT transfers', this.formatQtyCsv(st.totalStockOutTransfers)],
      [
        'Total OUT theoretical (Script + transfers)',
        this.formatQtyCsv(st.totalStockOutDisplay),
      ],
      ['Closing stock (Final, input)', this.formatQtyCsv(st.closingStockInput)],
      ['Real consumption (Real Out)', this.formatQtyCsv(st.realConsumption)],
      ['Theoretical closing stock', this.formatQtyCsv(st.closingTheoretical)],
      ['Closing variance qty (Final - Script)', this.formatQtyCsv(st.varianceQty)],
      [
        'Closing variance %',
        st.variancePct != null ? this.formatQtyCsv(st.variancePct) : '',
      ],
      ['Generated at', generatedAt],
      [''],
    ].map((row) => row.map((v) => this.escapeCSV(v)).join(','));

    const header = ['Date', 'Stock IN', 'Stock OUT', 'Adjustment'].join(',');

    const lines = daily.map((d) => {
      const dateStr = d.date || '';
      const baseIn =
        Number(d.stock_in_receipts || 0) +
        Number(d.stock_in_transfers || 0);
      const adj = Number(adjustmentsByDate[dateStr] || 0);
      const stockInDisplay = baseIn + adj;
      const stockOutDisplay =
        Number(d.stock_out_sales_theoretical || 0) +
        Number(d.stock_out_transfers || 0);

      return [
        this.escapeCSV(dateStr),
        this.formatQtyCsv(stockInDisplay),
        this.formatQtyCsv(stockOutDisplay),
        this.formatQtyCsv(adj),
      ].join(',');
    });

    const csv = [...summaryRows, header, ...lines].join('\r\n');

    const dateSafe = (asof || 'date').replace(/[^0-9-]/g, '');
    const locSafe = (locationLabel || 'location').replace(
      /[^a-z0-9-_]+/gi,
      '_'
    );
    const prodSafe = (productLabel || 'product').replace(
      /[^a-z0-9-_]+/gi,
      '_'
    );
    const fname =
      'RawMaterialConsumption_' +
      dateSafe +
      '_' +
      locSafe +
      '_' +
      prodSafe +
      '.csv';

    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fname;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---------- Toggles ----------

  toggleFiltersCollapsed = () => {
    this.setState(
      (prev) => ({ filtersCollapsed: !prev.filtersCollapsed }),
      this.updateTableLayout
    );
  };

  toggleSummaryCollapsed = () => {
    this.setState(
      (prev) => ({ summaryCollapsed: !prev.summaryCollapsed }),
      this.updateTableLayout
    );
  };

  // ---------- Render: Filters ----------

  renderFilters() {
    const st = this.state;
    const { filtersCollapsed } = st;
    const filters = st.filters || {};
    const locations = st.locations || [];
    const products = st.products || [];
    const productNameMap = st.productNameMap || {};

    const productOptions = products.map((p) => {
      const value = String(
        p.iref || p.uid || p.code || p.id || p.product || ''
      );
      const label =
        p.name || p.productName || p.description || p.code || value;
      return { value, label };
    });

    const selectedProdVal =
      filters && filters.product != null ? String(filters.product) : '';
    const productValue =
      productOptions.find((o) => o.value === selectedProdVal) || null;

    const selectedProductLabel =
      productNameMap[String(filters.product)] ||
      String(filters.product || '');

    return (
      <Paper
        elevation={1}
        sx={{
          flexShrink: 0,
          p: { xs: 0.5, sm: 0.75 },
          mb: 0.75,
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FilterAltIcon fontSize="small" />
            <Typography variant="subtitle2">Filters</Typography>
          </Box>
          <Button
            size="small"
            onClick={this.toggleFiltersCollapsed}
            startIcon={
              filtersCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />
            }
          >
            {filtersCollapsed ? 'Show' : 'Hide'}
          </Button>
        </Box>

        {!filtersCollapsed && (
          <Stack spacing={1} sx={{ mt: 0.75 }}>
            {/* Row 1: Location + Product, better aligned via Grid */}
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  fullWidth
                  label="Location"
                  value={filters.location}
                  onChange={(e) =>
                    this.setState((prev) => ({
                      filters: {
                        ...(prev.filters || {}),
                        location: e.target.value,
                      },
                    }))
                  }
                  size="small"
                  margin="dense"
                  sx={{
                    '& .MuiSelect-select': {
                      textAlign: 'left',
                    },
                    '& .MuiInputBase-root': {
                      height: 34,
                      borderRadius: '12px',
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'left',
                      fontSize: {
                        xs: '16px',
                        sm: '0.84rem',
                      },
                    },
                  }}
                >
                  <MenuItem value="">Select...</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem
                      key={String(loc.iref || loc.uid || loc.id)}
                      value={String(loc.iref || loc.uid || loc.id)}
                    >
                      {loc.name ||
                        loc.description ||
                        String(loc.iref || loc.uid || loc.id)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={7}>
                <Autocomplete
                  size="small"
                  value={productValue}
                  options={productOptions}
                  isOptionEqualToValue={(o, v) =>
                    ((o && o.value) || '') === ((v && v.value) || '')
                  }
                  onChange={(_e, opt) => {
                    const newVal = opt ? opt.value : '';
                    this.setState((prev) => ({
                      filters: {
                        ...(prev.filters || {}),
                        product: newVal,
                      },
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Product"
                      margin="dense"
                      sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                          height: 34,
                          borderRadius: '12px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: {
                            xs: '16px',
                            sm: '0.84rem',
                          },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textAlign: 'left',
                        },
                      }}
                      InputProps={{
                        ...(params.InputProps || {}),
                      }}
                      inputProps={{
                        ...(params.inputProps || {}),
                        title: selectedProductLabel,
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Row 2: Date + buttons */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexWrap: 'nowrap' }}
            >
              <TextField
                label="Date"
                type="date"
                value={filters.date}
                onChange={(e) =>
                  this.setState((prev) => ({
                    filters: {
                      ...(prev.filters || {}),
                      date: e.target.value,
                    },
                  }))
                }
                size="small"
                margin="dense"
                sx={{
                  width: { xs: 150, sm: 160 },
                  '& .MuiInputBase-root': {
                    height: 34,
                    borderRadius: '12px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: {
                      xs: '16px',
                      sm: '0.84rem',
                    },
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />

              <Box sx={{ flexGrow: 1 }} />

              <Button
                variant="contained"
                size="small"
                onClick={this.fetchData}
                disabled={this.state.loading}
                sx={{
                  height: 32,
                  borderRadius: '11px',
                  textTransform: 'none',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={this.exportCSV}
                disabled={!this.state.daily.length}
                sx={{
                  height: 32,
                  borderRadius: '11px',
                  textTransform: 'none',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                CSV
              </Button>
            </Stack>
          </Stack>
        )}

        {/* infoText intentionally hidden from UI; error shown */}
        {!!this.state.errorText && (
          <Alert severity="error" sx={{ mt: 0.75 }}>
            {this.state.errorText}
          </Alert>
        )}
      </Paper>
    );
  }

  // ---------- Render: Summary (compact, mobile-friendly) ----------
  renderSummary() {
    const {
      openingStock,
      theoreticalTotal,
      totalStockOutTransfers,
      totalStockInAdjusted,
      closingStockInput,
      realConsumption,
      varianceQty,
      variancePct,
      unitLabel,
      summaryCollapsed,
    } = this.state;

    const unitSuffix = unitLabel ? ` ${unitLabel}` : '';

    const formatWithUnit = (val) =>
      val != null && val !== ''
        ? `${this.formatQtyDisplay(val)}${unitSuffix}`
        : '';

    const kpiRow = (label, { value, isInput = false }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 24,
          width: '100%',
          gap: 0.75,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            flex: 1,                 // take remaining space
            textAlign: 'right',      // right align label
            pr: 1,                   // small padding to the value
            fontWeight: 600,
            color: 'primary.main',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            fontSize: {xs: '0.8rem', md: '0.9rem' },
          }}
        >
          {label}
        </Typography>

        {isInput ? (
          <TextField
            value={closingStockInput}
            onChange={(e) => this.onChangeClosing(e.target.value)}
            size="small"
            margin="dense"
            placeholder="0"
            sx={{
              width: { xs: 90, sm: 120, md: 150, lg: 180 },
              '& .MuiInputBase-root': {
                height: 24,
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiInputBase-input': {
                textAlign: 'right',
                fontSize: '1rem',
                padding: '0 4px',
              },
            }}
          />
        ) : (
          <Box
            sx={{
              width: { xs: 90, sm: 120, md: 150, lg: 180 },
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
              }}
            >
              {value || '-'}
            </Typography>
          </Box>
        )}
      </Box>
    );

    return (
      <Paper
        elevation={1}
        sx={{
          flexShrink: 0,
          p: { xs: 0.5, sm: 0.75, md: 1 },
          mb: 0.75,
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: summaryCollapsed ? 0 : 0.25,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <AssessmentIcon fontSize="small" />
            <Typography variant="subtitle2">Summary</Typography>
          </Box>
          <Button
            size="small"
            onClick={this.toggleSummaryCollapsed}
            startIcon={
              summaryCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />
            }
          >
            {summaryCollapsed ? 'Show' : 'Hide'}
          </Button>
        </Box>

        {summaryCollapsed ? null : (
          <Grid container spacing={0.5}>
            {/* Row 1: Initial / In */}
            <Grid item xs={6}>
              {kpiRow('Initial', {
                value: formatWithUnit(openingStock),
              })}
            </Grid>
            <Grid item xs={6}>
              {kpiRow('In', {
                value: formatWithUnit(totalStockInAdjusted),
              })}
            </Grid>

            {/* Row 2: Script Out / Real Out */}
            <Grid item xs={6}>
              {kpiRow('Out Script', {
                value: formatWithUnit(theoreticalTotal + totalStockOutTransfers),
              })}
            </Grid>
            <Grid item xs={6}>
              {kpiRow('Out Real', {
                value:
                  realConsumption != null
                    ? formatWithUnit(realConsumption)
                    : '',
              })}
            </Grid>

            {/* Row 3: Final (input) / Var */}
            <Grid item xs={6}>
              {kpiRow('Final', {
                value: formatWithUnit(closingStockInput),
                isInput: true,
              })}
            </Grid>
            <Grid item xs={6}>
              {kpiRow('Var', {
                value:
                  varianceQty != null
                    ? `${formatWithUnit(varianceQty)}${
                        variancePct != null
                          ? ` (${this.formatQtyDisplay(variancePct)}%)`
                          : ''
                      }`
                    : '',
              })}
            </Grid>
          </Grid>
        )}
      </Paper>
    );
  }

  // ---------- Render: Table ----------
  renderTable() {
    const {
      loading,
      daily,
      page,
      rowsPerPage,
      adjustmentsByDate,
      tableHeight,
    } = this.state;

    // rowsPerPage is always -1 => all rows, ignore page slicing for now
    const paged = daily;

    return (
      <Paper
        elevation={1}
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '14px',
        }}
      >
        <TableContainer
          ref={this.tableContainerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            '& .MuiTableCell-root': {
              px: { xs: 0.45, sm: 0.8 },
              py: { xs: 0.35, sm: 0.45 },
              fontSize: { xs: '0.74rem', sm: '0.86rem' },
            },
            '& .MuiTableCell-stickyHeader': {
              backgroundColor: '#f8fafc',
              zIndex: 3,
            },
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{ tableLayout: 'fixed' }}   // 👈 important
          >
            {/* Define fixed column widths */}
            <colgroup>
              <col style={{ width: '30%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>

            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  In
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Out
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Adj +/-
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paged.map((row, idx) => {
                const dateStr = row.date || '';
                const baseIn =
                  Number(row.stock_in_receipts || 0) +
                  Number(row.stock_in_transfers || 0);
                const adj = Number(adjustmentsByDate[dateStr] || 0);
                const stockInDisplay = baseIn + adj;

                const stockOutDisplay =
                  Number(row.stock_out_sales_theoretical || 0) +
                  Number(row.stock_out_transfers || 0);

                return (
                  <TableRow key={dateStr || String(idx)} hover>
                    <TableCell>{dateStr}</TableCell>
                    <TableCell align="right">
                      {this.formatQtyDisplay(stockInDisplay)}
                    </TableCell>
                    <TableCell align="right">
                      {this.formatQtyDisplay(stockOutDisplay)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{ overflow: 'hidden' }} // keep layout stable
                    >
                      {/* Wrap TextField in a fixed-width box so it doesn't resize */}
                      <Box
                        sx={{
                          width: { xs: 68, sm: 80, md: 100, lg: 120 },
                          ml: 'auto', // push it to the right inside cell
                        }}
                      >
                        <TextField
                          value={
                            adjustmentsByDate[dateStr] !== undefined
                              ? String(adjustmentsByDate[dateStr])
                              : ''
                          }
                          onChange={(e) =>
                            this.onChangeAdjustment(dateStr, e.target.value)
                          }
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 26,
                            },
                            '& input': {
                              py: 0.3,
                              fontSize: {
                                xs: '16px',
                                sm: '0.86rem',
                              },
                              padding: '0 4px',
                              textAlign: 'right',
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination bar stays visible; rowsPerPage fixed -1 (All) */}
        <TablePagination
          component="div"
          count={daily.length}
          page={0}
          onPageChange={() => {}}
          rowsPerPage={-1}
          rowsPerPageOptions={[]}
          sx={{
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            minHeight: { xs: 38, sm: 44 },
            overflow: 'hidden',
            '& .MuiTablePagination-toolbar': {
              px: { xs: 0.5, sm: 1 },
              minHeight: { xs: 38, sm: 44 },
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.72rem', sm: '0.84rem' },
              m: 0,
            },
          }}
          labelDisplayedRows={({ count }) =>
            count === -1 ? '0 of 0' : `1–${count} of ${count}`
          }
        />

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Paper>
    );
  }

  // ---------- Root render ----------

  render() {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'background.default',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderFilters()}
        {this.renderSummary()}
        {this.renderTable()}
      </Box>
    );
  }
}

export default RawMaterialConsumption;
